import React from 'react';
import { Link } from 'react-router-dom';
import { useActivity } from '../../hooks/useActivity';
import { formatDate } from '../../utils/helpers';
import { useTranslation } from 'react-i18next'

// Activity types
export const ACTIVITY_TYPES = {
    PROJECT_CREATED: 'project_created',
    PROJECT_UPDATED: 'project_updated',
    FUNDING_REQUESTED: 'funding_requested',
    FUNDING_APPROVED: 'funding_approved',
    FUNDING_REJECTED: 'funding_rejected',
    TASK_COMPLETED: 'task_completed',
    TASK_ASSIGNED: 'task_assigned',
    USER_JOINED: 'user_joined',
    COMMENT_added: 'comment_added',
    DOCUMENT_uploaded: 'document_uploaded',
    MILESTONE_REACHED: 'milestone_reached',
    BUDGET_UPDATE: 'budget_update'
};

// Mock activity data
const mockActivities = [
    {
        id: 1,
        type: ACTIVITY_TYPES.FUNDING_APPROVED,
        user: { name: 'Sarah Investor', avatar: 'SI' },
        message: 'approved funding for',
        target: { name: 'Data Analytics Dashboard', link: '/projects/3' },
        amount: '$45,000',
        timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString()
    },
    {
        id: 2,
        type: ACTIVITY_TYPES.TASK_COMPLETED,
        user: { name: 'Mike Johnson', avatar: 'MJ' },
        message: 'completed task',
        target: { name: 'Database Schema design', link: '/projects/1' },
        timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString()
    },
    {
        id: 3,
        type: ACTIVITY_TYPES.FUNDING_REQUESTED,
        user: { name: 'Mike Johnson', avatar: 'MJ' },
        message: 'requested funding for',
        target: { name: 'Mobile App development', link: '/funding/requests' },
        amount: '$320,000',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    },
    {
        id: 4,
        type: ACTIVITY_TYPES.PROJECT_UPDATED,
        user: { name: 'Abhi Consultant', avatar: 'AH' },
        message: 'updated project status to',
        target: { name: 'E-commerce Platform', link: '/projects/1' },
        extra: 'In Progress (65%)',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString()
    },
    {
        id: 5,
        type: ACTIVITY_TYPES.MILESTONE_REACHED,
        user: { name: 'Team', avatar: '🎯' },
        message: 'reached milestone',
        target: { name: 'Cloud Infrastructure Migration', link: '/projects/4' },
        extra: 'Project Completed',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
    },
    {
        id: 6,
        type: ACTIVITY_TYPES.COMMENT_added,
        user: { name: 'Lisa Finance', avatar: 'LF' },
        message: 'commented on',
        target: { name: 'Budget Review', link: '/finance/budget' },
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
        id: 7,
        type: ACTIVITY_TYPES.USER_JOINED,
        user: { name: 'Emily Consultant', avatar: 'EC' },
        message: 'joined the team as',
        extra: 'Frontend developer',
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    }
];

// Format relative time
function formatTimeAgo(timestamp) {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return formatDate(date);
}

// Get icon and color based on activity type
function getActivityStyle(type) {
    const styles = {
        [ACTIVITY_TYPES.PROJECT_CREATED]: {
            icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
            bg: 'bg-purple-100',
            color: 'text-purple-600',
            ring: 'ring-purple-50'
        },
        [ACTIVITY_TYPES.PROJECT_UPDATED]: {
            icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
            bg: 'bg-blue-100',
            color: 'text-blue-600',
            ring: 'ring-blue-50'
        },
        [ACTIVITY_TYPES.FUNDING_REQUESTED]: {
            icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
            bg: 'bg-amber-100',
            color: 'text-amber-600',
            ring: 'ring-amber-50'
        },
        [ACTIVITY_TYPES.FUNDING_APPROVED]: {
            icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
            bg: 'bg-green-100',
            color: 'text-green-600',
            ring: 'ring-green-50'
        },
        [ACTIVITY_TYPES.FUNDING_REJECTED]: {
            icon: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
            bg: 'bg-red-100',
            color: 'text-red-600',
            ring: 'ring-red-50'
        },
        [ACTIVITY_TYPES.TASK_COMPLETED]: {
            icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
            bg: 'bg-green-100',
            color: 'text-green-600',
            ring: 'ring-green-50'
        },
        [ACTIVITY_TYPES.TASK_ASSIGNED]: {
            icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
            bg: 'bg-indigo-100',
            color: 'text-indigo-600',
            ring: 'ring-indigo-50'
        },
        [ACTIVITY_TYPES.USER_JOINED]: {
            icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
            bg: 'bg-teal-100',
            color: 'text-teal-600',
            ring: 'ring-teal-50'
        },
        [ACTIVITY_TYPES.COMMENT_added]: {
            icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
            bg: 'bg-gray-100',
            color: 'text-gray-600',
            ring: 'ring-gray-50'
        },
        [ACTIVITY_TYPES.DOCUMENT_uploaded]: {
            icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
            bg: 'bg-cyan-100',
            color: 'text-cyan-600',
            ring: 'ring-cyan-50'
        },
        [ACTIVITY_TYPES.MILESTONE_REACHED]: {
            icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
            bg: 'bg-yellow-100',
            color: 'text-yellow-600',
            ring: 'ring-yellow-50'
        },
        [ACTIVITY_TYPES.BUDGET_UPDATE]: {
            icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
            bg: 'bg-emerald-100',
            color: 'text-emerald-600',
            ring: 'ring-emerald-50'
        }
    };

    return styles[type] || styles[ACTIVITY_TYPES.COMMENT_added];
}

// Single activity item
function ActivityItem({ activity, isLast }) {
    // Map backend ProjectActivityResponse to the styles
    const getStyle = (type) => {
        if (!type) return getActivityStyle(ACTIVITY_TYPES.COMMENT_added);
        if (type.includes('Approved')) return getActivityStyle(ACTIVITY_TYPES.FUNDING_APPROVED);
        if (type.includes('Rejected')) return getActivityStyle(ACTIVITY_TYPES.FUNDING_REJECTED);
        if (type.includes('Created')) return getActivityStyle(ACTIVITY_TYPES.PROJECT_CREATED);
        return getActivityStyle(ACTIVITY_TYPES.COMMENT_added);
    };
    
    const style = getStyle(activity.type);

    return (
        <div className="relative flex gap-3">
            {/* Timeline line */}
            {!isLast && (
                <div className="absolute left-[15px] top-8 w-0.5 h-[calc(100%-8px)] bg-gray-200"></div>
            )}

            {/* Icon */}
            <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full ${style.bg} ring-4 ${style.ring} flex items-center justify-center`}>
                <span className={`text-xs font-semibold ${style.color}`}>
                    {activity.title ? activity.title[0] : '🔔'}
                </span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pb-4">
                <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">{activity.title}</span>
                    {' '}
                    <span className="text-gray-500">{activity.description}</span>
                    {' '}
                    {activity.project_id && (
                        <Link to={`/projects/${activity.project_id}`} className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                            View Project
                        </Link>
                    )}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">{formatTimeAgo(activity.created_at)}</p>
            </div>
        </div>
    );
}

// Main ActivityFeed component
export default function ActivityFeed({
    projectId = null,
    title = 'Recent Activity',
    maxItems = 5,
    showViewAll = true,
    className = ''
}) {
  const { t } = useTranslation('ui')
    const { activities = [], loading } = useActivity(projectId);
    const displayedActivities = Array.isArray(activities) ? activities.slice(0, maxItems) : [];

    return (
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                </div>
                {showViewAll && (
                    <button className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline">
                        View all
                    </button>
                )}
            </div>

            {/* Activity List */}
            <div className="px-5 py-4">
                {loading ? (
                    <div className="py-8 text-center text-gray-500 italic">{t('loading_activity')}</div>
                ) : displayedActivities.length > 0 ? (
                    <div>
                        {displayedActivities.map((activity, index) => (
                            <ActivityItem
                                key={activity.id}
                                activity={activity}
                                isLast={index === displayedActivities.length - 1}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="py-8 text-center text-gray-400">
                        No activity found.
                    </div>
                )}
            </div>
        </div>
    );
}
